// Consent-gated loading for Google Analytics and Meta Pixel. Both used to be
// static <script> tags in index.html that fired unconditionally on every
// page load — this module makes that opt-in instead, per the privacy policy
// promise that visitors can decline third-party tracking. The site's own
// first-party analytics (analytics_events, via useTrackEvent) is unaffected;
// this only gates the two third-party trackers.

const CONSENT_KEY = "analytics-consent";
const GA_MEASUREMENT_ID = "G-7RJEQQQKYS";
const META_PIXEL_ID = "1236240332902169";

export type ConsentValue = "granted" | "denied";

export function getStoredConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

export function setStoredConsent(value: ConsentValue) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // ignore (private browsing / storage blocked)
  }
  if (value === "granted") {
    loadGoogleAnalytics();
    loadMetaPixel();
  }
}

let gaLoaded = false;
export function loadGoogleAnalytics() {
  if (gaLoaded || document.getElementById("ga-gtag-script")) return;
  gaLoaded = true;

  const script = document.createElement("script");
  script.id = "ga-gtag-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => {
    window.dataLayer!.push(args);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);
}

let pixelLoaded = false;
export function loadMetaPixel() {
  if (pixelLoaded || window.fbq) return;
  pixelLoaded = true;

  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq?.("init", META_PIXEL_ID);
  window.fbq?.("track", "PageView");
}

/** Call once on app start: if the visitor already granted consent on a previous visit, load the trackers immediately. */
export function initThirdPartyAnalyticsIfConsented() {
  if (getStoredConsent() === "granted") {
    loadGoogleAnalytics();
    loadMetaPixel();
  }
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}
